// SPDX-FileCopyrightText: Szilárd Hajba
// SPDX-License-Identifier: LGPL-3.0-or-later

/**
 * Portable `cl:` reference helpers.
 *
 * A Cloudillo resource is addressed by `<ownerIdTag>:<fileId>`, which is
 * globally portable: every instance can resolve it because the owner is
 * embedded. The browser URL is NOT portable (each user has their own
 * app-domain), but the *path* portion is. These helpers convert between the
 * current route and a shareable `cl:` reference.
 *
 * Two reference shapes:
 *  - Short document form `cl:<appId>/<owner>:<fileId>[?nav=…]` — the context
 *    is dropped (the owner in the resId makes it portable; the recipient opens
 *    it in their own `~` home). `access` is stripped (recipients get their own).
 *  - Lossless full form `cl:/<full-path>` — for any other route (built-in
 *    pages, community-context routes, profiles). Note the leading slash after
 *    the colon disambiguates it from the short form.
 *
 * Pure module — no React, no shell state — so it is unit-testable in isolation.
 */

import { docRef } from '@cloudillo/core'

import { contextPath, HOME_BASE } from './routes.js'

/**
 * True for a genuine in-app absolute path. Rejects protocol-relative (`//host`)
 * and backslash-escaped (`/\host`) forms that browsers resolve cross-origin —
 * passing those to `navigate()` would attempt an off-origin pushState.
 */
function isSafeAppPath(path: string): boolean {
	return path.startsWith('/') && path[1] !== '/' && path[1] !== '\\'
}

/**
 * Build a portable `cl:` reference for the given route.
 *
 * @param pathname - The route path (e.g. `/~/app/quillo/bob.org:abc123`)
 * @param search - Optional query string (with or without leading `?`)
 */
export function buildRef(pathname: string, search = ''): string {
	const query = search && !search.startsWith('?') ? `?${search}` : search
	const params = new URLSearchParams(query)

	// Document route: /<ctx>/app/<appId>/<resId…> where resId carries an owner.
	const m = pathname.match(/^\/([^/]+)\/app\/([^/]+)\/(.+)$/)
	if (m) {
		const appId = m[2]
		const resId = m[3]
		if (resId.includes(':')) {
			// Short, portable form. Drop context + access; preserve nav.
			return docRef(appId, resId, params.get('nav'))
		}
	}

	// Lossless full form for everything else.
	return `cl:${pathname}${query}`
}

/**
 * Resolve a `cl:` reference, a full `http(s)://` URL, or a bare `/path` into a
 * route path suitable for `navigate()`.
 *
 * @returns The route path, or `null` if the input is unparseable.
 */
export function resolveRef(input: string): string | null {
	const trimmed = input.trim()
	if (!trimmed) return null

	if (trimmed.startsWith('cl:')) {
		const body = trimmed.slice(3)
		// Leading slash => lossless full path form.
		if (body.startsWith('/')) return isSafeAppPath(body) ? body : null
		// Short document form: <appId>/<owner>:<fileId>[?…]
		const m = body.match(/^([^/]+)\/(.+)$/)
		if (!m) return null
		// The tail came out of a route already URL-encoded, so it goes in through the
		// low-level builder rather than being re-encoded.
		const queryAt = m[2].indexOf('?')
		const tail = queryAt < 0 ? m[2] : m[2].slice(0, queryAt)
		const query = queryAt < 0 ? '' : m[2].slice(queryAt)
		return contextPath(HOME_BASE, 'app', `${m[1]}/${tail}`) + query
	}

	if (/^https?:\/\//i.test(trimmed)) {
		try {
			const u = new URL(trimmed)
			// Same guard as the other two branches: `https://x//evil` yields `//evil`,
			// which `navigate()` would resolve off-origin.
			const path = u.pathname + u.search
			return isSafeAppPath(path) ? path : null
		} catch {
			return null
		}
	}

	// A bare path is already a portable route path (the embedded `~` resolves
	// to this viewer's home). Used when extracting the pathname of a pasted URL.
	if (trimmed.startsWith('/')) return isSafeAppPath(trimmed) ? trimmed : null

	return null
}

/**
 * True when `input` should be handled as a reference by the omnibox.
 *
 * A bare leading `/` is command mode, NOT a ref — the canonical shareable
 * forms are `cl:…` and full `http(s)://` URLs. (`resolveRef` still accepts a
 * bare `/path` because it extracts the pathname from a pasted full URL.)
 */
export function isRefLike(input: string): boolean {
	return /^(cl:|https?:\/\/)/i.test(input.trim())
}

// vim: ts=4
